import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link2 } from "lucide-react";
import { getStoreId } from "../utils/auth";

const apiUrl = (import.meta.env.VITE_BACKEND_URL || "").replace(/\/$/, "");

function defaultReauthUrl() {
  return apiUrl ? `${apiUrl}/install` : "/install";
}

/**
 * True when an Instant Site / manage_instant_site reconnect is needed.
 */
export function isInstantSiteAuthError(message) {
  const m = String(message || "").toLowerCase();
  if (!m) return false;
  return (
    m.includes("manage_instant_site") ||
    m.includes("instant site") ||
    m.includes("scope_required") ||
    m.includes("instantsite") ||
    (m.includes("redirect") && (m.includes("403") || m.includes("forbidden") || m.includes("400")))
  );
}

/**
 * Banner + reconnect CTA when Instant Site token/scope is missing.
 * Existing merchants stay in the app; External OAuth (/install) grants updated scopes
 * without requiring uninstall.
 */
export default function InstantSiteReconnectBanner({ forceShow = false, hintError = null }) {
  const { t } = useTranslation();
  const hashSiteId = getStoreId();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(hashSiteId));

  const load = useCallback(async () => {
    if (!hashSiteId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${apiUrl}/api/auth/token-status?hashSiteId=${encodeURIComponent(hashSiteId)}`,
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setStatus(data);
      } else {
        setStatus(null);
      }
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [hashSiteId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (forceShow || isInstantSiteAuthError(hintError)) {
      load();
    }
  }, [forceShow, hintError, load]);

  const needsReconnect =
    forceShow ||
    isInstantSiteAuthError(hintError) ||
    status?.needsReauth === true ||
    status?.instantSiteOk === false;

  if (loading && !needsReconnect && !forceShow && !hintError) {
    return null;
  }

  if (!needsReconnect) {
    return null;
  }

  const reauthUrl = status?.reauthUrl || defaultReauthUrl();

  const openReconnect = () => {
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = reauthUrl;
        return;
      }
    } catch {
      // cross-origin
    }
    window.location.href = reauthUrl;
  };

  return (
    <div
      className="alert alert-warning d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3"
      role="status"
    >
      <div className="small mb-0" style={{ maxWidth: 640 }}>
        <div className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <Link2 size={16} aria-hidden />
          {t("instantSite.reconnectTitle")}
        </div>
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <button type="button" className="btn btn-sm btn-warning" onClick={openReconnect}>
          {t("instantSite.reconnectCta")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={load}
          disabled={loading}
        >
          {t("instantSite.recheck")}
        </button>
      </div>
    </div>
  );
}
